import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, MessageSquare, Info, Phone, Mail, X, Users } from 'lucide-react';
import mapi from './api';
import { isPortalArea } from './areas';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C, FilterChips, AppBottomNav } from './MobileShell';
import { initialsFromName } from './homeMenu';
import { splitPhoneNumbers, toTelHref } from '@/components/PhoneLink';
import { matchesFreeText } from '@/components/search/SearchBuilder';
import { nestUnterrichtContacts } from './contactNest';

function filterNested(nested, query) {
  const rows = [];
  nested.rows.forEach((row) => {
    const studentHit = matchesFreeText(row.student, query);
    const guardians = row.guardians.filter((guardian) => studentHit || matchesFreeText(guardian, query));
    if (studentHit || guardians.length) {
      rows.push({ student: row.student, guardians: studentHit ? row.guardians : guardians });
    }
  });
  return {
    rows,
    orphans: nested.orphans.filter((contact) => matchesFreeText(contact, query)),
  };
}

function AppSheet({ onClose, children, testId }) {
  // Portal an den Body: sonst hängt das Fenster am Ende der langen Liste
  // und liegt hinter der Tab-Leiste, also außerhalb des sichtbaren Bereichs.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4"
      style={{
        background: 'rgba(0,0,0,0.6)',
        paddingTop: 'calc(5.5rem + env(safe-area-inset-top, 0px))',
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl p-5"
        style={{ background: C.card }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function ContactRow({ contact, testId, indent, onOpen, onInfo, onChat, chatTestId }) {
  const phone = splitPhoneNumbers(contact.phone)[0];
  return (
    <div className="flex items-center px-4 py-3" data-testid={testId}
         style={{ background: C.card, borderBottom: `1px solid ${C.border}`, paddingLeft: indent ? '2.5rem' : undefined }}>
      <span className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold"
            style={{ background: C.rose, color: C.red }}>
        {initialsFromName(contact.name)}
      </span>
      <button type="button" className="flex-1 min-w-0 text-left" onClick={onOpen}
              data-testid={onOpen ? `mobile-contact-expand-${contact.person_id}` : undefined}>
        <p className="text-sm font-medium truncate">
          {contact.name}
          {contact.is_me && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: C.red }}>Das bist Du!</span>}
        </p>
        <p className="text-xs truncate" style={{ color: C.dim }}>
          {contact.person_type === 'guardian'
            ? 'Eltern'
            : (contact.subjects?.length ? contact.subjects.join(', ') : (contact.user_id ? 'registriert' : 'nicht registriert'))}
        </p>
      </button>
      <button type="button" onClick={onInfo} className="p-2" style={{ color: C.dim }}
              data-testid={`mobile-contact-info-${contact.person_id || contact.user_id}`}>
        <Info className="w-4 h-4" />
      </button>
      <button type="button" onClick={onChat} className="p-2"
              style={{ color: contact.user_id ? C.red : C.border }}
              data-testid={chatTestId || `mobile-contact-chat-${contact.person_id || contact.user_id}`}>
        <MessageSquare className="w-5 h-5" />
      </button>
      {phone && (
        <a href={toTelHref(phone) || `tel:${phone}`}
           className="p-2" style={{ color: C.red }}
           data-testid={`mobile-contact-phone-${contact.person_id || contact.user_id}`}
           aria-label={`Anrufen ${contact.name}`}>
          <Phone className="w-5 h-5" />
        </a>
      )}
    </div>
  );
}

const TABS = [
  { k: 'KONTAKTE', label: 'Alle', groups: ['VERWALTUNG', 'UNTERRICHT', 'TEAM'] },
  { k: 'UNTERRICHT', label: 'Schüler', groups: ['UNTERRICHT'] },
  { k: 'TEAM', label: 'Lehrkräfte', groups: ['TEAM'] },
  { k: 'GRUPPEN', label: 'Gruppen', groups: [] },
];

function storedArea() {
  try {
    return localStorage.getItem('app_area') || '';
  } catch (_e) {
    return '';
  }
}

export default function MobileContacts() {
  const nav = useNavigate();
  const mobile = useMobile();
  const portal = isPortalArea(mobile?.area || storedArea());
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState('KONTAKTE');
  const [open, setOpen] = useState({});
  const [expandedStudents, setExpandedStudents] = useState({});
  const [composer, setComposer] = useState(null);
  const [detail, setDetail] = useState(null);
  const [hideUnregistered, setHideUnregistered] = useState(false);
  const [q, setQ] = useState('');
  const [courses, setCourses] = useState([]);

  const load = useCallback(async () => {
    try {
      const people = await mapi.get('/app/contacts');
      const groups = portal
        ? { data: [] }
        : await mapi.get('/messages/course-directory').catch(() => ({ data: [] }));
      setContacts(people.data || []);
      setCourses(groups.data || []);
    } catch (e) { toast.error('Kontakte konnten nicht geladen werden'); }
  }, [portal]);
  useEffect(() => { load(); }, [load]);

  const openCourse = async (course) => {
    try {
      const r = await mapi.post('/messages/conversations/from-course', { course_id: course.id });
      nav(`/m/chat/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gruppe konnte nicht angeschrieben werden'); }
  };

  const startChat = async (people) => {
    const targets = (Array.isArray(people) ? people : [people]).filter((person) => person?.user_id);
    if (!targets.length) {
      const name = (Array.isArray(people) ? people[0] : people)?.name;
      toast.info(`${name || 'Dieser Kontakt'} ist noch nicht in der App registriert`);
      return;
    }
    try {
      const r = targets.length === 1
        ? await mapi.post('/messages/conversations', { kind: 'direct', participant_ids: [targets[0].user_id] })
        : await mapi.post('/messages/conversations', {
          kind: 'group',
          participant_ids: targets.map((person) => person.user_id),
          title: targets.map((person) => person.name).filter(Boolean).join(', '),
        });
      setComposer(null);
      nav(`/m/chat/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Chat konnte nicht gestartet werden'); }
  };

  const activeGroups = TABS.find((t) => t.k === tab)?.groups || [];
  const visibleCourses = courses.filter((course) => matchesFreeText(course, q));
  const inTab = contacts
    .filter((c) => activeGroups.includes(c.group))
    .filter((c) => !hideUnregistered || c.user_id);
  const nested = tab === 'TEAM'
    ? { rows: [], orphans: [] }
    : filterNested(nestUnterrichtContacts(inTab.filter((c) => c.group === 'UNTERRICHT')), q);
  const grouped = {};
  inTab.filter((c) => c.group !== 'UNTERRICHT' && matchesFreeText(c, q)).forEach((c) => {
    if (c.group === 'TEAM') {
      const subs = c.subject_categories?.length ? c.subject_categories : ['Ohne Fachbereich'];
      subs.forEach((s) => (grouped[s] = grouped[s] || []).push(c));
    } else {
      (grouped[c.group] = grouped[c.group] || []).push(c);
    }
  });
  const groupEntries = Object.entries(grouped);
  if (tab === 'TEAM') {
    groupEntries.sort(([a], [b]) => (a === 'Ohne Fachbereich') - (b === 'Ohne Fachbereich') || a.localeCompare(b, 'de'));
  }
  const availableTeamTab = contacts.some((c) => c.group === 'TEAM');
  const studentRows = nested.rows;
  const showStudents = tab !== 'TEAM' && tab !== 'GRUPPEN';

  return (
    <MobileShell bottomNav={<AppBottomNav />}>
      <MobileHeader title="Kontakte" onRefresh={load} backTo="/m" />
      <FilterChips
        testIdPrefix="mobile-contacts-tab"
        value={tab}
        onChange={setTab}
        items={TABS.filter((t) => t.k !== 'TEAM' || availableTeamTab).map((t) => ({ k: t.k, label: t.label }))}
      />

      <div className="px-4 pt-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…"
               data-testid="mobile-contacts-search"
               className="w-full rounded-lg px-3 py-2 text-sm outline-none"
               style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }} />
      </div>

      {tab !== 'GRUPPEN' && (
      <label className="flex items-center gap-2 px-4 py-2 text-xs" style={{ color: C.dim }}>
        <input type="checkbox" checked={hideUnregistered}
               onChange={(e) => setHideUnregistered(e.target.checked)}
               data-testid="mobile-contacts-hide-unregistered" />
        Nicht registrierte Kontakte ausblenden
      </label>
      )}

      <div className="flex-1 overflow-y-auto pb-3">
        {tab === 'GRUPPEN' && (
          visibleCourses.length === 0 ? (
            <p className="text-center text-sm mt-10" style={{ color: C.dim }}>Keine Gruppen vorhanden</p>
          ) : visibleCourses.map((course) => (
            <div key={course.id} className="flex items-center px-4 py-3"
                 style={{ background: C.card, borderBottom: `1px solid ${C.border}` }}
                 data-testid={`mobile-contact-course-${course.id}`}>
              <span className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                    style={{ background: C.rose, color: C.red }}>
                <Users className="h-5 w-5" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{course.label}</p>
                <p className="text-xs truncate" style={{ color: C.dim }}>
                  {course.participant_count === 1 ? '1 Teilnehmer' : `${course.participant_count || 0} Teilnehmer`}
                </p>
              </div>
              <button type="button" onClick={() => openCourse(course)} className="p-2" style={{ color: C.red }}
                      data-testid={`mobile-contact-course-chat-${course.id}`} aria-label={`Nachricht an ${course.label}`}>
                <MessageSquare className="w-5 h-5" />
              </button>
            </div>
          ))
        )}
        {tab !== 'GRUPPEN' && (
        <>
        {studentRows.length === 0 && nested.orphans.length === 0 && groupEntries.length === 0 && (
          <p className="text-center text-sm mt-10" style={{ color: C.dim }}>
            {tab === 'UNTERRICHT' ? 'Keine Belegungen vorhanden' : 'Keine Kontakte vorhanden'}
          </p>
        )}
        {showStudents && studentRows.map((row) => {
          const studentId = row.student.person_id;
          const isOpen = !!expandedStudents[studentId];
          return (
            <div key={studentId}>
              <ContactRow
                contact={row.student}
                testId={`mobile-contact-student-${studentId}`}
                onOpen={() => setExpandedStudents((prev) => ({ ...prev, [studentId]: !prev[studentId] }))}
                onInfo={() => setDetail(row.student)}
                onChat={() => setComposer(row)}
              />
              {isOpen && row.guardians.map((guardian) => (
                <ContactRow
                  key={`${studentId}-${guardian.person_id}`}
                  contact={guardian}
                  indent
                  testId={`mobile-contact-guardian-${studentId}-${guardian.person_id}`}
                  onInfo={() => setDetail(guardian)}
                  onChat={() => startChat(guardian)}
                  chatTestId={`mobile-contact-chat-${studentId}-${guardian.person_id}`}
                />
              ))}
            </div>
          );
        })}
        {showStudents && nested.orphans.map((guardian) => (
          <ContactRow
            key={`orphan-${guardian.person_id}`}
            contact={guardian}
            testId={`mobile-contact-guardian-${guardian.person_id}`}
            onInfo={() => setDetail(guardian)}
            onChat={() => startChat(guardian)}
          />
        ))}
        {groupEntries.map(([g, list]) => {
          const isOpen = open[g] !== false;
          return (
            <div key={g}>
              <button onClick={() => setOpen((p) => ({ ...p, [g]: !isOpen }))}
                      data-testid={`mobile-contacts-group-${g}`}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
                      style={{ background: C.bg, color: C.red, borderBottom: `1px solid ${C.border}` }}>
                {g} ({list.length})
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {isOpen && list.map((c, i) => (
                <ContactRow
                  key={`${g}-${c.person_id || c.user_id}-${i}`}
                  contact={c}
                  testId={`mobile-contact-${c.person_type}-${c.person_id || c.user_id}`}
                  onInfo={() => setDetail(c)}
                  onChat={() => startChat(c)}
                />
              ))}
            </div>
          );
        })}
        </>
        )}
      </div>

      {composer && (
        <AppSheet onClose={() => setComposer(null)} testId="mobile-contact-message-sheet">
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold">Nachricht an {composer.student.name}</h3>
              <button type="button" onClick={() => setComposer(null)}><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold"
                    style={{ background: C.bg, border: `1px solid ${C.border}` }}
                    onClick={() => startChat(composer.student)}>
              Nur den Schüler
            </button>
            {composer.guardians.length > 1 && composer.guardians.map((guardian) => (
              <button key={guardian.person_id} type="button"
                      className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold"
                      style={{ background: C.bg, border: `1px solid ${C.border}` }}
                      onClick={() => startChat(guardian)}>
                Nur {guardian.name}
              </button>
            ))}
            {composer.guardians.length > 0 && (
              <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold"
                      style={{ background: C.bg, border: `1px solid ${C.border}` }}
                      onClick={() => startChat(composer.guardians)}>
                Nur die Eltern
              </button>
            )}
            {composer.guardians.length > 0 && (
              <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold"
                      style={{ background: C.bg, border: `1px solid ${C.border}` }}
                      onClick={() => startChat([composer.student, ...composer.guardians])}>
                Schüler und Eltern
              </button>
            )}
          </div>
        </AppSheet>
      )}

      {detail && (
        <AppSheet onClose={() => setDetail(null)} testId="mobile-contact-detail">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">{detail.name}</h3>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            {splitPhoneNumbers(detail.phone).map((number, index) => (
              <a key={`${number}-${index}`}
                 href={toTelHref(number) || `tel:${number}`}
                 className="flex items-center gap-2 py-2 text-sm" style={{ color: C.text }}
                 data-testid={index === 0 ? 'mobile-contact-call' : `mobile-contact-call-${index + 1}`}>
                <Phone className="w-4 h-4" style={{ color: C.red }} />{number}
              </a>
            ))}
            {detail.email && (
              <a href={`mailto:${detail.email}`} className="flex items-center gap-2 py-2 text-sm" style={{ color: C.text }}>
                <Mail className="w-4 h-4" style={{ color: C.red }} />{detail.email}
              </a>
            )}
            {detail.subjects?.length > 0 && (
              <p className="text-xs mt-2" style={{ color: C.dim }}>Fächer: {detail.subjects.join(', ')}</p>
            )}
            {!detail.phone && !detail.email && !detail.subjects?.length && (
              <p className="text-sm" style={{ color: C.dim }}>Keine weiteren Informationen hinterlegt.</p>
            )}
          </div>
        </AppSheet>
      )}

    </MobileShell>
  );
}
